import { useHistory } from "react-router-dom"
import React, { useEffect, useRef } from "react"
import { useCurrentEvent } from "queries/events"
import useModal from "services/useModal"
import {
  Account, AccountUser, ModalName, User,
} from "sharedTypes"
import EnrichDisabled from "modals/EnrichDisabled"
import ExportGuests from "modals/ExportGuests"
import ExportInvitationList from "modals/ExportInvitationList"
import ImportGuests from "modals/ImportGuests"
import NewGuest from "modals/NewGuest"
import NewPlusOnes from "modals/NewPlusOnes"
import GuestListForm from "modals/GuestListForm"
import SegmentForm from "modals/SegmentForm"
import SendTestEmail from "modals/Emails/SendTestEmail"
import ScheduleSendEmail from "modals/Emails/ScheduleSendEmail"
import AccountChooser from "modals/SwitchAccount"
import TableForm from "modals/TableForm"
import UpgradePlan from "modals/UpgradePlan"
import WebPageURL from "modals/WebPageURL"
import GoogleAnalyticsModal from "modals/GoogleAnalyticsModal"
import EventCapacity from "modals/EventCapacity"
import EventLink from "modals/EventLink/EventLink"
import AccountUserForm from "modals/AccountUserForm"
import SelectGuest from "modals/SelectGuest"
import ChangeStatus from "pages/EventShow/Guests/MultipleSelectActions/Action/ChangeStatus"
import FormForm from "modals/FormForm/FormForm"
import QuestionModal from "modals/QuestionModal"
import NewEmail from "modals/Emails/NewEmail"
import AddFormQuestion from "modals/FormForm/AddFormQuestion"
import NewTextMessage from "modals/NewTextMessage/NewTextMessage"
import PreviewTextMessage from "modals/TextMessage/PreviewTextMessage/PreviewTextMessage"
import EditTextRecipients from "modals/EditTextRecipients"
import EditFieldModal from "modals/EditFieldModal"
import ExportTextReport from "modals/ExportTextReport"
import InviteExhibitorOrAssignLicense from "modals/InviteExhibitorOrAssignLicense"
import InviteExhibitorToPurchase from "modals/InviteExhibitorToPurchase"
import UpgradeSuccessful from "modals/UpgradeSuccessful"
import PaymentSuccessful from "modals/PaymentSuccessful"
import ViewGuestLinks from "modals/ViewGuestLinks/ViewGuestLinks"
import CloneEvent from "modals/CloneEvent"
import ValidationWarningModal from "modals/BadgeBuilder/ValidationWarningModal"
import EditTemplateDimensionsModal from "modals/BadgeBuilder/EditTemplateDimensionsModal"
import EditPrinterLabelModal from "modals/BadgeBuilder/EditPrinterLabelModal"
import PublicEventTitleModal from "modals/PublicEventTitleModal"
import WhatsAppSendingOptions from "modals/WhatsAppSendingOptions/WhatsAppSendingOptions"
import GuestCommunicationPreferences from "modals/GuestCommunicationPreferences/GuestCommunicationPreferences"
import EventActivityForm from "../../modals/EventActivityForm"

const BadgeTemplateBrowser = React.lazy(() => import("modals/BadgeBuilder/BadgeTemplateBrowser/BadgeTemplateBrowser"))

type Props = {
  currentAccount: Account
  currentUser: User
  currentModal: { name: ModalName, params?: Record<string, any>}
  teamMembers: AccountUser[]
}

const Modals = ({
  currentAccount, currentUser, currentModal, teamMembers,
}: Props) => {
  const history = useHistory()
  const { data: currentEvent } = useCurrentEvent()
  const showModal = useModal()

  const onHide = () => showModal(null)

  const unblock = useRef(() => {
  })

  useEffect(() => {
    // @ts-ignore
    unblock.current = history.block((transition, action) => {
      if (action === "PUSH") {
        return true
      }

      onHide()

      return false
    })

    return () => {
      unblock.current()
      unblock.current = () => {
      }
    }
  }, [])

  switch (currentModal?.name) {
    case ModalName.EnrichDisabled:
      return <EnrichDisabled onHide={onHide} />
    case ModalName.ExportGuests:
      return (
        <ExportGuests
          eventId={currentEvent.id}
          eventGroups={currentEvent.eventGroups}
          segments={currentEvent.segments}
          counts={currentEvent.counts}
          onHide={onHide}
          currentUser={currentUser}
        />
      )
    case ModalName.ExportInvitationList:
      return (
        <ExportInvitationList
          eventId={currentEvent.id}
          emailId={currentModal?.params?.id}
          emailAddress={currentUser.email}
          onHide={onHide}
        />
      )
    case ModalName.ExportTextReport:
      return (
        <ExportTextReport
          eventId={currentEvent.id}
          textMessageId={currentModal?.params?.id}
          emailAddress={currentUser.email}
          onHide={onHide}
        />
      )
    case ModalName.ImportGuests:
      return (
        <ImportGuests
          eventId={currentEvent.id}
          guestsCount={currentEvent.counts.guests}
          importLimit={currentEvent.nonTicketedGuestLimit}
          onClose={onHide}
        />
      )
    case ModalName.NewEmail:
      return (
        <NewEmail event={currentEvent} onClose={onHide} />
      )
    case ModalName.EditEmailName:
      return (
        <EditFieldModal
          onHide={onHide}
          title="Email Name"
          subtitle="This title is for internal use and won't be visible to your guests."
          onSubmit={currentModal.params?.onSubmit}
          fieldName="title"
          initialValues={currentModal.params?.initialValues}
        />
      )
    case ModalName.EditTextMessageName:
      return (
        <EditFieldModal
          onHide={onHide}
          title="Text Message Name"
          subtitle="This title is for internal use and won't be visible to your guests."
          onSubmit={currentModal.params?.onSubmit}
          fieldName="name"
          initialValues={currentModal.params?.initialValues}
        />
      )
    case ModalName.ScheduleSendEmail:
      return (
        <ScheduleSendEmail
          event={currentEvent}
          email={currentModal.params?.email}
          onHide={onHide}
          onSave={currentModal.params?.onSave}
        />
      )
    case ModalName.NewGuest:
      return (
        <NewGuest
          eventId={currentEvent.id}
          parentId={currentModal?.params?.parentId}
          firstName={currentModal?.params?.firstName}
          lastName={currentModal?.params?.lastName}
          onHide={onHide}
        />
      )
    case ModalName.NewPlusOnes:
      return (
        <NewPlusOnes
          eventId={currentEvent.id}
          guestId={currentModal?.params?.guestId}
          plusOnesCount={currentModal?.params?.plusOnesCount}
          guestName={currentModal?.params?.guestName}
          onHide={onHide}
        />
      )
    case ModalName.GuestListForm:
      return (
        <GuestListForm
          eventId={currentEvent.id}
          eventGroupId={currentModal?.params?.eventGroupId}
          defaultTitle={currentModal?.params?.defaultTitle}
          openOnCheckInAlert={currentModal?.params?.openOnCheckInAlert}
          eventGroups={currentEvent.eventGroups}
          users={currentEvent.users}
          teamMembers={teamMembers}
          onHide={onHide}
        />
      )
    case ModalName.SegmentForm:
      return (
        <SegmentForm
          eventId={currentEvent.id}
          segmentsEnabled={currentEvent.segmentsEnabled}
          segmentId={currentModal?.params?.segmentId}
          segments={currentEvent.segments}
          eventGroups={currentEvent.eventGroups}
          onHide={onHide}
        />
      )
    case ModalName.SendTestEmail:
      return (
        <SendTestEmail
          eventId={currentEvent.id}
          emailId={currentModal?.params?.id}
          emailAddress={currentUser.email}
          onHide={onHide}
        />
      )
    case ModalName.SwitchAccount:
      return (
        <AccountChooser
          currentUser={currentUser}
          accounts={currentUser.accounts}
          hide={onHide}
          newAccount={currentModal?.params?.newAccount}
        />
      )
    case ModalName.TableForm:
      return (
        <TableForm
          eventId={currentEvent.id}
          tableId={currentModal?.params?.id}
          tables={currentEvent.tables}
          onHide={onHide}
        />
      )
    case ModalName.Upgrade:
      return (
        <UpgradePlan
          currentAccount={currentAccount}
          onHide={onHide}
          defaultSelectedPlan={currentModal?.params?.defaultSelectedPlan}
        />
      )
    case ModalName.WebPageURL:
      return (
        <WebPageURL
          eventId={currentEvent.id}
          urlPath={currentEvent.website?.urlPath}
          onHide={onHide}
          redirectPath={currentModal?.params?.redirectPath}
        />
      )
    case ModalName.EventLink:
      return <EventLink event={currentEvent} onHide={onHide} />
    case ModalName.GoogleAnalytics:
      return (
        <GoogleAnalyticsModal
          eventId={currentEvent.id}
          googleAnalyticsMeasurementId={currentEvent.googleAnalyticsMeasurementId || null}
          onHide={onHide}
        />
      )
    case ModalName.EventCapacity:
      return (
        <EventCapacity
          event={currentEvent}
          languageTemplateId={currentModal.params?.languageTemplateId}
          onHide={onHide}
        />
      )
    case ModalName.PublicEventTitle:
      return (
        <PublicEventTitleModal
          onHide={onHide}
          event={currentEvent}
          updateEvent={currentModal.params?.updateEvent}
          onChangeEventWorkingCopy={currentModal.params?.onChangeEventWorkingCopy || (() => {})}
          onResetEventWorkingCopy={currentModal.params?.onResetEventWorkingCopy || (() => {})}
        />
      )
    case ModalName.AccountUserForm:
      return (
        <AccountUserForm
          currentUser={currentUser}
          {...currentModal.params}
          onClose={onHide}
        />
      )
    case ModalName.SelectGuest:
      return (
        <SelectGuest
          eventId={currentEvent.id}
          filtered={currentModal.params?.filtered}
          onSelect={currentModal.params?.onSelect}
          {...currentModal.params}
          onClose={onHide}
        />
      )
    case ModalName.UpdateGuestStatus:
      return (
        <ChangeStatus
          guest={currentModal.params?.guest}
          updateGuest={currentModal.params?.updateGuest}
          selectedStatusValue={currentModal.params?.selectedStatusValue}
          onClose={onHide}
        />
      )
    case ModalName.FormForm:
      return (
        <FormForm
          onClose={onHide}
          event={currentEvent}
          onSuccess={currentModal.params?.onSuccess}
          form={currentModal.params?.form}
        />
      )
    case ModalName.QuestionForm:
      return (
        <QuestionModal
          onClose={onHide}
          eventId={currentEvent.id}
          questionId={currentModal.params?.questionId}
          onSuccess={currentModal.params?.onSuccess}
        />
      )
    case ModalName.AddFormQuestion:
      return (
        <AddFormQuestion
          onClose={onHide}
          event={currentEvent}
          blocks={currentModal.params?.blocks}
          addBlock={currentModal.params?.addBlock}
          selectedQuestionId={currentModal.params?.selectedQuestionId}
          position={currentModal.params?.position}
        />
      )
    case ModalName.NewTextMessage:
      return (
        <NewTextMessage
          event={currentEvent}
          channel={currentModal.params?.channel}
          initialMessageType={currentModal.params?.initialMessageType}
          onClose={onHide}
        />
      )
    case ModalName.BadgeTemplateBrowser:
      return (
        <React.Suspense fallback={null}>
          <BadgeTemplateBrowser
            onHide={onHide}
            eventId={currentEvent.id}
            onTemplateCreated={currentModal?.params?.onTemplateCreated}
          />
        </React.Suspense>
      )
    case ModalName.EventActivityForm:
      return (
        <EventActivityForm
          eventId={currentEvent.id}
          editLocation={currentModal.params?.editLocation}
          onSave={currentModal.params?.onSave}
          defaultName={currentModal.params?.defaultName}
          onHide={onHide}
        />
      )
    case ModalName.EditTextRecipients:
      return (
        <EditTextRecipients
          onHide={onHide}
          event={currentEvent}
          onSave={currentModal.params?.onSave}
          textMessage={currentModal.params?.textMessage}
        />
      )
    case ModalName.PreviewTextMessage:
      return (
        <PreviewTextMessage
          onHide={onHide}
          event={currentEvent}
          textMessage={currentModal.params?.textMessage}
          enabled={currentModal.params?.enabled}
        />
      )
    case ModalName.InviteExhibitorOrAssignLicense:
      return (
        <InviteExhibitorOrAssignLicense
          eventId={currentEvent.id}
          onHide={onHide}
        />
      )
    case ModalName.InviteExhibitorToPurchase:
      return (
        <InviteExhibitorToPurchase
          eventId={currentEvent.id}
          onHide={onHide}
        />
      )
    case ModalName.UpgradeSuccessful:
      return (
        <UpgradeSuccessful
          onHide={onHide}
        />
      )
    case ModalName.PaymentSuccessful:
      return <PaymentSuccessful onHide={onHide} />
    case ModalName.ViewGuestLinks:
      return (
        <ViewGuestLinks
          onHide={onHide}
          guest={currentModal.params?.guest}
          eventId={currentModal.params?.eventId}
        />
      )
    case ModalName.CloneEvent:
      return (
        <CloneEvent
          onHide={onHide}
          guestCount={currentModal.params?.guestCount}
          cloneEvent={currentModal.params?.cloneEvent}
        />
      )
    case ModalName.RenameBadgeTemplate:
      return (
        <EditFieldModal
          onHide={onHide}
          title="Badge Template Name"
          subtitle="Enter a name for your badge template. This name will also appear in the mobile app when printing badges."
          onSubmit={currentModal.params?.onSubmit}
          fieldName="name"
          initialValues={currentModal.params?.initialValues}
        />
      )
    case ModalName.BadgeTemplateSaveValidationWarning:
      return (
        <ValidationWarningModal
          isOpen
          onClose={onHide}
          onSaveWithWarnings={currentModal.params?.onSaveWithWarnings}
          onCancel={onHide}
          issues={currentModal.params?.issues || []}
        />
      )
    case ModalName.EditBadgeTemplateDimensions:
      return (
        <EditTemplateDimensionsModal
          isOpen
          onClose={onHide}
          template={currentModal.params?.template}
          onTemplateChange={currentModal.params?.onTemplateChange}
        />
      )
    case ModalName.EditBadgeTemplatePrinterAndMedia:
      return (
        <EditPrinterLabelModal
          isOpen
          onClose={onHide}
          template={currentModal.params?.template}
          onTemplateChange={currentModal.params?.onTemplateChange}
        />
      )
    case ModalName.WhatsAppSendingOptions:
      return <WhatsAppSendingOptions onClose={onHide} />
    case ModalName.GuestCommunicationPreferences:
      return (
        <GuestCommunicationPreferences
          guest={currentModal.params?.guest}
          onClose={onHide}
        />
      )
    default:
      return null
  }
}

export default Modals
